import { type ProjectCode } from '@constants'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Image } from '@pets-ui/Image/Image'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useMemo } from 'react'
import { useActiveBrands, useStmQuickOrderStore } from '../../../hooks'
import { useFastOrderBrands } from '../../hooks/use-fast-order-brands'
import { buildBrandSelectOptions, resolveSelectedProjectCode } from './build-brand-select-options'

const BRAND_ICON_SIZE = 32
const SELECT_MIN_WIDTH = 128

const styles = stylex.create({
  trigger: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    height: 32,
  },
})

export const BrandSelect = () => {
  const activeBrands = useActiveBrands()
  const { fastOrderBrands, isLoading: isLoadingFastOrderBrands } = useFastOrderBrands()
  const selectedProjectCode = useStmQuickOrderStore(state => state.selectedProjectCode)
  const setSelectedProjectCode = useStmQuickOrderStore(state => state.setSelectedProjectCode)
  const options = useMemo(() => buildBrandSelectOptions(fastOrderBrands, activeBrands), [fastOrderBrands, activeBrands])
  const selectedOption = options.find(option => option.projectCode === selectedProjectCode)

  // API 回來前選項是登入品牌的 fallback，不在這階段覆寫，否則會先切一次再切回
  useEffect(function syncSelectionToOptions() {
    if (isLoadingFastOrderBrands) return
    const resolvedProjectCode = resolveSelectedProjectCode(options, selectedProjectCode)
    if (resolvedProjectCode) setSelectedProjectCode(resolvedProjectCode)
  }, [isLoadingFastOrderBrands, options, selectedProjectCode, setSelectedProjectCode])

  if (!options.length) return null

  return (
    <HStack gap={2} alignItems="center" py={4}>
      {selectedOption?.iconSrc && <Image src={selectedOption.iconSrc} alt={selectedOption.label} width={BRAND_ICON_SIZE} height={BRAND_ICON_SIZE} />}
      <Box minWidth={SELECT_MIN_WIDTH}>
        <Select<ProjectCode> fullWidth value={selectedProjectCode} onChange={setSelectedProjectCode} xStyles={{ select: styles.trigger }}>
          {options.map(option => (
            <SelectOption key={option.projectCode} value={option.projectCode}>{option.label}</SelectOption>
          ))}
        </Select>
      </Box>
    </HStack>
  )
}
